import api from './api';

const BASE_PATH = '/admin/analytics';

/**
 * Helper to trigger download of exported file
 */
const downloadExport = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const analyticsAPI = {
  getDashboardMetrics: async (params) => {
    const response = await api.get(`${BASE_PATH}/dashboard/`, { params });
    return response.data;
  },

  // Points Ledger report — async by design. A month runs to ~270k rows and takes
  // ~45s to build, past nginx's 60s default timeout, so this returns a job_id
  // immediately and the caller polls getPointsLedgerStatus for the download URL.
  exportPointsLedger: async ({ month }) => {
    const response = await api.get('/admin/reports/points-ledger/', {
      params: { month },
    });
    return response.data;
  },

  getPointsLedgerStatus: async (jobId) => {
    const response = await api.get('/admin/reports/points-ledger-status/', {
      params: { job_id: jobId },
    });
    return response.data;
  },

  // Loyalty Analytics — Transactions + Exceptions sheets in one workbook, one
  // calendar month per file. Same job/poll cycle as the ledger; a big month is
  // ~500k rows and takes minutes, so it never comes back inline.
  exportAnalytics: async ({ month }) => {
    const response = await api.get('/admin/reports/analytics/', {
      params: { month },
    });
    return response.data;
  },

  getAnalyticsStatus: async (jobId) => {
    const response = await api.get('/admin/reports/analytics-status/', {
      params: { job_id: jobId },
    });
    return response.data;
  },

  // Product Cost Master — only ~474 rows, so it comes back as the file itself
  // rather than a background job. pos: 'marn' | 'lyncs' | 'all'.
  exportProductCostMaster: async ({ pos }) => {
    const response = await api.get('/admin/reports/product-cost-master/', {
      params: { pos },
      responseType: 'blob',
    });
    return response;
  },

  generateReport: async (data) => {
    const response = await api.post(`${BASE_PATH}/generate-report/`, data);
    return response.data;
  },

  exportReport: async (reportId, format) => {
    const response = await api.post(
      `${BASE_PATH}/generate-report/`,
      {
        report_id: reportId,
        format,
        export: true,
      }
    );
    return response.data;
  },

  /**
   * Export data to CSV, Excel, or PDF
   * Returns a blob that can be downloaded
   */
  exportData: async (request) => {
    const response = await api.post(
      `${BASE_PATH}/export/`,
      request,
      {
        responseType: 'blob',
      }
    );
    return response.data;
  },

  /**
   * Export and download data in one call
   */
  exportAndDownload: async (dataType, format, filters) => {
    const blob = await analyticsAPI.exportData({
      data_type: dataType,
      format,
      filters,
    });

    const extension = format === 'xlsx' ? 'xlsx' : format === 'pdf' ? 'pdf' : 'csv';
    const filename = `${dataType}_export_${new Date().toISOString().split('T')[0]}.${extension}`;

    downloadExport(blob, filename);
  },

  getPredictiveAnalytics: async (params) => {
    const response = await api.get(`${BASE_PATH}/predictive/`, { params });
    return response.data;
  },
};
